import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  UsersThree,
  MagnifyingGlass,
  Check,
  X,
  PaperPlaneTilt,
  CaretLeft,
  Trash,
  CircleNotch,
  ChatCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listFriends,
  requestFriend,
  acceptFriend,
  rejectFriend,
  removeFriend,
  listFriendMessages,
  sendFriendMessage,
  markFriendRead,
  formatApiError,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_MS = 5000;

const fmtAgo = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/**
 * FriendsDialog
 * - Tabs: Friends · Requests (badge with incoming count) · Add
 * - Clicking a friend row opens a DM chat panel within the dialog
 * - Friends roster polled every 5 s; per-chat messages polled every 5 s
 */
export default function FriendsDialog({ open, onOpenChange }) {
  const { user: me } = useAuth();
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [initialLoading, setInitialLoading] = useState(true);
  const [tab, setTab] = useState("friends"); // friends | requests | add
  const [activeChat, setActiveChat] = useState(null); // friendship object

  // refresh() — call on initial open + every poll. Critically, this does
  // NOT flip a loading flag so the inputs / chat panel never unmount
  // mid-typing during the background poll (was causing the flicker +
  // "deletes what I type" bug).
  const refresh = useCallback(async (showLoading = false) => {
    if (showLoading) setInitialLoading(true);
    try {
      const r = await listFriends();
      setData(r);
    } catch (e) {
      // Surface only on the very first failure — quiet on background
      // polls so we don't toast-spam the user on transient hiccups.
      if (showLoading) toast.error(formatApiError(e?.response?.data?.detail) || "Could not load friends");
    } finally {
      if (showLoading) setInitialLoading(false);
    }
  }, []);

  // Initial fetch + polling while open.
  useEffect(() => {
    if (!open) return;
    refresh(true);
    const t = setInterval(() => refresh(false), POLL_MS);
    return () => clearInterval(t);
  }, [open, refresh]);

  // Default to "Requests" tab on open if there are incoming requests waiting.
  useEffect(() => {
    if (open && data.incoming.length > 0 && tab === "friends") {
      setTab("requests");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data.incoming.length]);

  const handleAccept = async (fid) => {
    try {
      await acceptFriend(fid);
      toast.success("Friend request accepted");
      refresh(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not accept");
    }
  };

  const handleReject = async (fid) => {
    try {
      await rejectFriend(fid);
      toast.success("Request declined");
      refresh(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not reject");
    }
  };

  const handleRemove = async (fid, name) => {
    if (!window.confirm(`Remove ${name || "this friend"}? Your chat history will also be deleted.`)) return;
    try {
      await removeFriend(fid);
      toast.success("Friend removed");
      if (activeChat?.id === fid) setActiveChat(null);
      refresh(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="friends-dialog"
        className="rounded-none border border-cyan-500/30 bg-[#0F1115] text-white max-w-2xl p-0 max-h-[88vh] grid grid-rows-[auto_minmax(0,1fr)] gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-cyan-500/20 bg-cyan-500/[0.04]">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-cyan-300 flex items-center gap-2">
            <UsersThree size={14} weight="fill" /> Friends
            <span className="text-zinc-500 ml-1 text-[10px] tracking-wider font-normal">
              {data.friends.length} friend{data.friends.length === 1 ? "" : "s"}
              {data.incoming.length > 0 ? ` · ${data.incoming.length} pending` : ""}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {activeChat ? (
            <FriendChat
              friendship={activeChat}
              me={me}
              onBack={() => setActiveChat(null)}
              onRemove={() => handleRemove(activeChat.id, activeChat.friend?.name)}
            />
          ) : (
            <>
              {/* tabs */}
              <div className="flex gap-1 border-b border-white/10 pb-2" data-testid="friends-tabs">
                <TabBtn id="friends" current={tab} setTab={setTab} label="Friends" count={data.friends.length} />
                <TabBtn
                  id="requests"
                  current={tab}
                  setTab={setTab}
                  label="Requests"
                  count={data.incoming.length}
                  highlight={data.incoming.length > 0}
                />
                <TabBtn id="add" current={tab} setTab={setTab} label="Add" />
              </div>

              {initialLoading && (
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 text-center py-3">
                  Loading…
                </div>
              )}

              {!initialLoading && tab === "friends" && (
                <FriendsList
                  friends={data.friends}
                  onOpenChat={(f) => setActiveChat(f)}
                  onRemove={handleRemove}
                />
              )}
              {!initialLoading && tab === "requests" && (
                <RequestsList
                  incoming={data.incoming}
                  outgoing={data.outgoing}
                  onAccept={handleAccept}
                  onReject={handleReject}
                />
              )}
              {!initialLoading && tab === "add" && <AddFriendForm onAdded={() => refresh(false)} />}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({ id, current, setTab, label, count, highlight }) {
  return (
    <button
      type="button"
      data-testid={`friends-tab-${id}`}
      onClick={() => setTab(id)}
      className={`mono uppercase tracking-widest text-[10px] px-3 py-1.5 border ${
        current === id
          ? "border-cyan-400 text-cyan-300 bg-cyan-400/10"
          : "border-white/10 text-zinc-400 hover:text-white"
      }`}
    >
      {label}
      {typeof count === "number" && count > 0 && (
        <span
          className={`ml-1.5 inline-flex items-center justify-center text-[9px] px-1 ${
            highlight ? "bg-red-500 text-white" : "bg-white/10 text-zinc-300"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function FriendsList({ friends, onOpenChat, onRemove }) {
  if (friends.length === 0) {
    return (
      <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 text-center py-8">
        — no friends yet —<br />
        <span className="normal-case tracking-normal text-[12px] text-zinc-400 mt-2 inline-block">
          Use the <strong>Add</strong> tab to send your first request.
        </span>
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="friends-list">
      {friends.map((f) => (
        <div
          key={f.id}
          data-testid={`friend-row-${f.id}`}
          className="flex items-center gap-3 border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-400/[0.04] px-3 py-2 cursor-pointer transition-colors"
          onClick={() => onOpenChat(f)}
        >
          <span
            className={`inline-block w-2 h-2 rounded-full shrink-0 ${
              f.friend.online ? "bg-emerald-400" : "bg-zinc-600"
            }`}
            title={f.friend.online ? "Online" : `Last seen ${fmtAgo(f.friend.last_seen_at)}`}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] truncate flex items-center gap-2">
              {f.friend.name || "—"}
              {f.unread_count > 0 && (
                <span
                  data-testid={`friend-unread-${f.id}`}
                  className="bg-red-500 text-white text-[9px] mono font-bold min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full"
                  title={`${f.unread_count} unread`}
                >
                  {f.unread_count > 9 ? "9+" : f.unread_count}
                </span>
              )}
            </div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-600">
              {f.friend.online ? "Online" : `Last seen ${fmtAgo(f.friend.last_seen_at)}`}
            </div>
          </div>
          <Button
            data-testid={`friend-chat-${f.id}`}
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChat(f);
            }}
            className="rounded-none h-7 mono uppercase text-[9px] tracking-widest border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10"
          >
            <ChatCircle size={11} className="mr-1" /> Chat
          </Button>
          <Button
            data-testid={`friend-remove-${f.id}`}
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(f.id, f.friend.name);
            }}
            className="rounded-none h-7 mono uppercase text-[9px] tracking-widest border-red-500/40 text-red-300 hover:bg-red-500/10"
            title="Remove friend"
          >
            <Trash size={11} />
          </Button>
        </div>
      ))}
    </div>
  );
}

function RequestsList({ incoming, outgoing, onAccept, onReject }) {
  if (incoming.length === 0 && outgoing.length === 0) {
    return (
      <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 text-center py-8">
        — no pending requests —
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="requests-list">
      {incoming.length > 0 && (
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-amber-300 mb-2">
            Incoming ({incoming.length})
          </div>
          <div className="space-y-1.5">
            {incoming.map((f) => (
              <div
                key={f.id}
                data-testid={`incoming-${f.id}`}
                className="flex items-center gap-3 border border-amber-500/30 bg-amber-500/[0.04] px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] truncate">{f.friend.name || "—"}</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-600">
                    Sent {fmtAgo(f.created_at)}
                  </div>
                </div>
                <Button
                  data-testid={`accept-${f.id}`}
                  size="sm"
                  onClick={() => onAccept(f.id)}
                  className="rounded-none h-7 mono uppercase text-[9px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black"
                >
                  <Check size={11} className="mr-1" /> Accept
                </Button>
                <Button
                  data-testid={`reject-${f.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => onReject(f.id)}
                  className="rounded-none h-7 mono uppercase text-[9px] tracking-widest border-white/15"
                >
                  <X size={11} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {outgoing.length > 0 && (
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
            Outgoing ({outgoing.length})
          </div>
          <div className="space-y-1.5">
            {outgoing.map((f) => (
              <div
                key={f.id}
                data-testid={`outgoing-${f.id}`}
                className="flex items-center gap-3 border border-white/10 px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] truncate">{f.friend.name || "—"}</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-600">
                    Awaiting response · sent {fmtAgo(f.created_at)}
                  </div>
                </div>
                <Button
                  data-testid={`cancel-${f.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => onReject(f.id)}
                  className="rounded-none h-7 mono uppercase text-[9px] tracking-widest border-white/15"
                  title="Cancel request"
                >
                  Cancel
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AddFriendForm({ onAdded }) {
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = q.trim();
    if (!v) return;
    setSubmitting(true);
    try {
      await requestFriend(v);
      toast.success(`Request sent to ${v}`);
      setQ("");
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not send request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-cyan-400/30 p-3 space-y-2" data-testid="add-friend-form">
      <div className="mono text-[10px] uppercase tracking-widest text-cyan-300">
        <MagnifyingGlass size={11} className="inline mr-1" /> Add a friend
      </div>
      <Input
        data-testid="add-friend-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Name or email — exact match"
        required
        minLength={2}
        maxLength={80}
        className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-cyan-500/30"
      />
      <Button
        data-testid="add-friend-submit"
        type="submit"
        disabled={submitting}
        className="rounded-none mono uppercase text-[10px] tracking-widest bg-cyan-500 hover:bg-cyan-400 text-black disabled:opacity-50"
      >
        {submitting ? <CircleNotch size={11} className="animate-spin mr-1.5" /> : <PaperPlaneTilt size={11} className="mr-1.5" />}
        Send Request
      </Button>
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-600 leading-relaxed">
        We'll look up the exact name (case-insensitive) or email. If the player isn't found, ask them for the exact name they registered with.
      </div>
    </form>
  );
}

function FriendChat({ friendship, me, onBack, onRemove }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await listFriendMessages(friendship.id);
      setMessages(r.messages || []);
      // Stamp the read watermark so the HUD badge clears. Fire-and-forget.
      markFriendRead(friendship.id).catch(() => {});
    } catch (e) {
      // Silent — keeps the user in the chat even on transient hiccups.
      console.warn("Could not load chat", e);
    }
  }, [friendship.id]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const v = draft.trim();
    if (!v) return;
    setSending(true);
    try {
      const msg = await sendFriendMessage(friendship.id, v);
      setMessages((m) => [...m, msg]);
      setDraft("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="friend-chat">
      <div className="flex items-center gap-2 border-b border-white/10 pb-2">
        <button
          type="button"
          data-testid="chat-back-btn"
          onClick={onBack}
          className="mono uppercase tracking-widest text-[10px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
        >
          <CaretLeft size={12} /> Back
        </button>
        <span
          className={`inline-block w-2 h-2 rounded-full shrink-0 ${
            friendship.friend.online ? "bg-emerald-400" : "bg-zinc-600"
          }`}
        />
        <div className="font-semibold flex-1 truncate">{friendship.friend.name || "—"}</div>
        <Button
          data-testid="chat-remove-friend"
          size="sm"
          variant="outline"
          onClick={onRemove}
          className="rounded-none h-7 mono uppercase text-[9px] tracking-widest border-red-500/40 text-red-300 hover:bg-red-500/10"
        >
          <Trash size={11} className="mr-1" /> Remove
        </Button>
      </div>
      <div
        ref={scrollRef}
        data-testid="chat-messages"
        className="h-[45vh] overflow-y-auto bg-black/30 border border-white/10 px-3 py-2 space-y-1.5"
      >
        {messages.length === 0 ? (
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 text-center py-8">
            No messages yet — say hi 👋
          </div>
        ) : (
          messages.map((m) => {
            const isMe = m.sender_user_id === me?.user_id;
            return (
              <div
                key={m.id}
                data-testid={`msg-${m.id}`}
                className={`flex ${isMe ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[70%] px-2.5 py-1.5 text-[13px] leading-snug break-words ${
                    isMe
                      ? "bg-cyan-500/15 border border-cyan-500/30 text-cyan-50"
                      : "bg-white/5 border border-white/10 text-zinc-100"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{m.body}</div>
                  <div className="mono text-[8px] uppercase tracking-widest opacity-50 mt-1">
                    {fmtAgo(m.sent_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <Input
          data-testid="chat-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a message…"
          maxLength={1000}
          className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-cyan-500/30 flex-1"
        />
        <Button
          data-testid="chat-send"
          type="submit"
          disabled={sending || !draft.trim()}
          className="rounded-none mono uppercase text-[10px] tracking-widest bg-cyan-500 hover:bg-cyan-400 text-black disabled:opacity-50"
        >
          {sending ? <CircleNotch size={11} className="animate-spin" /> : <PaperPlaneTilt size={11} />}
        </Button>
      </form>
    </div>
  );
}
